import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Eye, Plus } from "lucide-react";
import { Link } from "wouter";
import type { Quote } from "@shared/schema";
import { usePermissions } from "@/hooks/usePermissions";
import { useMemo } from "react";

const RECENT_QUOTES_KEY = "recentlyViewedQuotes";

function getRecentInteractions(): Record<string, number> {
  try {
    const stored = localStorage.getItem(RECENT_QUOTES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function trackQuoteView(quoteId: number) {
  try {
    const interactions = getRecentInteractions();
    interactions[quoteId.toString()] = Date.now();
    localStorage.setItem(RECENT_QUOTES_KEY, JSON.stringify(interactions));
  } catch {
    // Ignore storage errors
  }
}

function formatPlanType(planType: string): string {
  if (!planType) return "";
  return planType
    .split(/[\s_-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function RecentQuotes() {
  const { can, isLoading: permissionsLoading } = usePermissions();
  const hasPermission = can("quotes:view");

  const { data: quotes = [], isLoading } = useQuery<Quote[]>({
    queryKey: ["/api/quotes"],
    enabled: hasPermission,
  });

  const recentQuotes = useMemo(() => {
    const interactions = getRecentInteractions();
    return [...(quotes ?? [])]
      .sort((a, b) => {
        const aTime = interactions[a.id.toString()] || 0;
        const bTime = interactions[b.id.toString()] || 0;
        if (aTime !== bTime) {
          return bTime - aTime;
        }
        return new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime();
      })
      .slice(0, 5);
  }, [quotes]);

  // Don't render if user doesn't have permission
  if (!permissionsLoading && !hasPermission) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
      case "sent": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "viewed": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "accepted": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "rejected": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      case "expired": return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString();
  };

  const isExpiringSoon = (validUntil: string | Date | null) => {
    if (!validUntil) return false;
    const expiryDate = new Date(validUntil);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-medium">Recent Quotes</CardTitle>
          <FileText className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Recent Quotes</CardTitle>
        <FileText className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {recentQuotes.length === 0 ? (
          <div className="text-center py-6">
            <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 mb-4">No quotes yet</p>
            <Link href="/quoting">
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Create Quote
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {recentQuotes.map((quote) => (
              <div key={quote.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {quote.companyName}
                    </p>
                    <Badge className={`text-xs ${getStatusColor(quote.status)}`}>
                      {quote.status}
                    </Badge>
                    {isExpiringSoon(quote.validUntil) && (
                      <Badge className="text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300">
                        Expiring Soon
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center text-xs text-gray-500 mt-1 space-x-4">
                    <span>{formatPlanType(quote.planType)}</span>
                    <span>{quote.employeeCount} employees</span>
                    <span>Valid until {formatDate(quote.validUntil)}</span>
                  </div>
                </div>
                <Link href="/quoting">
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ))}
            <div className="pt-2 border-t">
              <Link href="/quoting">
                <Button variant="ghost" size="sm" className="w-full">
                  View All Quotes ({quotes.length})
                </Button>
              </Link>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
